/**
 * Venue photo addresses (pure). Photos live in the public guide-media
 * bucket (1606): `/storage/v1/object/public/guide-media/<path>` is the file
 * as uploaded, and Supabase's image service resizes it on request at
 * `/storage/v1/render/image/public/guide-media/<path>?width=…`, so cards
 * never download a 2400-pixel original. Files from /public (scan stills)
 * and anything else are served as they are.
 */

const PUBLIC_OBJECT = /^(https:\/\/[^/]+)\/storage\/v1\/object\/public\/guide-media\/(.+)$/;

/** The widths a photo is offered at; the browser picks by the slot's `sizes`. */
export const GUIDE_PHOTO_WIDTHS = [480, 960, 1600] as const;

/** The resizable part of a public guide-media address, or null for anything else. */
export function guidePhotoParts(src: string): { origin: string; path: string } | null {
  const match = src.match(PUBLIC_OBJECT);
  return match ? { origin: match[1], path: match[2] } : null;
}

/** The photo at `width` pixels (JPEG/WebP as the browser accepts); the original when it cannot be resized. */
export function guidePhotoAt(src: string, width: number, quality = 75): string {
  const parts = guidePhotoParts(src);
  if (!parts) return src;
  return `${parts.origin}/storage/v1/render/image/public/guide-media/${parts.path}?width=${width}&quality=${quality}`;
}

/** A `srcset` across the standard widths, or null when the photo cannot be resized. */
export function guidePhotoSrcSet(src: string): string | null {
  if (!guidePhotoParts(src)) return null;
  return GUIDE_PHOTO_WIDTHS.map((w) => `${guidePhotoAt(src, w)} ${w}w`).join(", ");
}

/** Public, stable address of a stored photo (no signing, no expiry). */
export function guidePublicPhotoUrl(supabaseUrl: string, path: string): string {
  return `${supabaseUrl.replace(/\/+$/, "")}/storage/v1/object/public/guide-media/${path}`;
}

/** The 1200-pixel version for share cards and structured data; the original for anything that cannot be resized. */
export function guideShareImage(src: string): string {
  return guidePhotoAt(src, 1200, 80);
}
